import { Search } from './icons.tsx'
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cx } from './cx.ts'

/** A keyboard key hint, e.g. <Kbd>Ctrl K</Kbd>. */
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cx(
        'inline-flex min-w-5 items-center justify-center rounded-lg border border-b-2 border-border bg-surface px-1.5 font-sans text-[11px] font-semibold text-muted',
        className,
      )}
    >
      {children}
    </kbd>
  )
}

export interface CommandItem {
  id: string
  label: ReactNode
  /** Second line, e.g. price and stock. */
  detail?: ReactNode
  icon?: ReactNode
  /** Shortcut shown on the right. */
  shortcut?: string
  onSelect: () => void
}

export interface CommandGroup {
  label: string
  items: CommandItem[]
}

export interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  query: string
  onQuery: (query: string) => void
  groups: CommandGroup[]
  placeholder?: string
  empty?: ReactNode
}

/** Search-and-jump dialog: a combobox over a grouped listbox (↑/↓, Enter, Esc). */
export function CommandPalette(props: CommandPaletteProps) {
  if (!props.open) return null
  return createPortal(<Palette {...props} />, document.body)
}

function Palette({
  onClose,
  query,
  onQuery,
  groups,
  placeholder = 'Search pages, products, customers…',
  empty = 'No matches',
}: CommandPaletteProps) {
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const baseId = useId()
  const items = groups.flatMap((g) => g.items)
  const [active, setActive] = useState(0)
  const [lastQuery, setLastQuery] = useState(query)
  // Back to the first result whenever the query changes.
  if (lastQuery !== query) {
    setLastQuery(query)
    setActive(0)
  }
  const index = Math.min(active, Math.max(items.length - 1, 0))
  const optionId = (i: number) => `${baseId}-o${i}`

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    input.current?.focus()
    return () => previous?.focus()
  }, [])

  useEffect(() => {
    list.current
      ?.querySelector(`[id="${CSS.escape(optionId(index))}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  })

  const select = (item: CommandItem | undefined) => {
    if (!item) return
    onClose()
    item.onSelect()
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive(items.length ? (index + 1) % items.length : 0)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(items.length ? (index - 1 + items.length) % items.length : 0)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      select(items[index])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  let n = -1
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-3 pt-[10vh] backdrop-blur-[2px]"
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
        className="flex max-h-[75vh] w-full max-w-xl flex-col overflow-hidden rounded-card border border-border bg-raised shadow-modal"
      >
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search className="size-5 shrink-0 text-muted" />
          <input
            ref={input}
            role="combobox"
            aria-expanded="true"
            aria-controls={`${baseId}-list`}
            aria-activedescendant={items.length ? optionId(index) : undefined}
            aria-label="Search"
            value={query}
            placeholder={placeholder}
            onChange={(e) => onQuery(e.target.value)}
            onKeyDown={onKeyDown}
            className="h-14 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-subtle"
          />
          <Kbd>Esc</Kbd>
        </div>
        <div
          ref={list}
          id={`${baseId}-list`}
          role="listbox"
          aria-label="Results"
          className="overflow-y-auto p-2"
        >
          {items.length === 0 && (
            <p className="px-3 py-8 text-center text-sm text-muted">{empty}</p>
          )}
          {groups
            .filter((g) => g.items.length > 0)
            .map((g) => (
              <div key={g.label} role="group" aria-label={g.label}>
                <div className="px-3 pb-1 pt-3 text-section text-muted">{g.label}</div>
                {g.items.map((item) => {
                  const i = ++n
                  const selected = i === index
                  return (
                    <div
                      key={item.id}
                      id={optionId(i)}
                      role="option"
                      aria-selected={selected}
                      onMouseMove={() => setActive(i)}
                      onClick={() => select(item)}
                      className={cx(
                        'flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5',
                        selected && 'bg-primary-light',
                      )}
                    >
                      {item.icon && (
                        <span
                          className={cx(
                            'flex size-8 shrink-0 items-center justify-center rounded-lg',
                            selected ? 'bg-surface text-primary' : 'bg-soft text-muted',
                          )}
                        >
                          {item.icon}
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{item.label}</span>
                        {item.detail && (
                          <span className="block truncate text-xs text-muted">{item.detail}</span>
                        )}
                      </span>
                      {item.shortcut && <Kbd>{item.shortcut}</Kbd>}
                    </div>
                  )
                })}
              </div>
            ))}
        </div>
        <div className="hidden gap-4 border-t border-border bg-background px-4 py-2 text-xs text-muted sm:flex">
          <span>
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> move
          </span>
          <span>
            <Kbd>↵</Kbd> open
          </span>
          <span>
            <Kbd>?</Kbd> all shortcuts
          </span>
        </div>
      </div>
    </div>
  )
}
